import {useEffect, useState} from 'react';

/**
 * Custom React hook to debounce value updates. Useful for consuming search input updates, for example.
 *
 * @note The value is not be updated on the leading edge of a change. Use `useThrottle` if that is required
 * functionality.
 *
 * @note The delay is refreshed each time the value updates, meaning if there are many changes over a long
 * time span, the value might not get updated at all until the end of that time. If you need a more consistent
 * update rate (for example, limit value updates to once per second), see `useThrottle`.
 * The storybook entry for `useThrottle` shows the difference between the two.
 *
 * @example
 * ```
 * function MyComponent(){
 *   const [fastChangingValue, setFastChangingValue] = useState(...);
 *   const debouncedValue = useDebounce(fastChangingValue, 1000);
 *
 *   return (
 *      <>
 *        <FastUpdater value={fastChangingValue} onChange={setFastChangingValue} />
 *        <UsesSlowChangingValue value={debouncedValue} />
 *      </>
 *   )
 * }
 * ```
 *
 * Initial version from: https://usehooks.com/useDebounce/
 *
 * @param {Type} T - shape of the value that is being debounced
 * @param {number} delay - milliseconds to wait before returning `value`
 * @return {T} value - the debounced value
 * @note - Make sure to memoize `value`s for which shallow equality is not sufficient, or `useDebounce` will
 * trigger unnecessary rerenders in the calling component.
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cancel the timeout if value changes (also on delay change or unmount)
    // This is how we prevent debounced value from updating if value is changed
    // within the delay period. Timeout gets cleared and restarted.
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
